import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ScrollText } from 'lucide-react'
import { PageHead } from '../AdminLayout'
import { Panel, DataTable, Pager, Toolbar, Select, SearchBox, RefreshButton, Tag, Drawer } from '../ui'
import { get, qs } from '../../lib/api'
import { fa, faDate } from '../../lib/format'

const LABELS: Record<string, string> = {
  'user.update': 'ویرایش کاربر', 'user.block': 'مسدودسازی کاربر', 'user.unblock': 'رفع مسدودی',
  'user.locks': 'تغییر قفل‌ها', 'user.reset2fa': 'بازنشانی ۲FA', 'user.password': 'تغییر رمز کاربر',
  'user.balance': 'تعدیل موجودی', 'user.cancelOrders': 'لغو سفارش‌های کاربر',
  'user.revokeApiKey': 'حذف کلید API', 'user.notify': 'ارسال پیام',
  'finance.rial.approve': 'تأیید برداشت ریالی', 'finance.rial.reject': 'رد برداشت ریالی',
  'finance.chain.approve': 'تأیید برداشت رمزارز', 'finance.chain.reject': 'رد برداشت رمزارز',
  'finance.chain.retry': 'تلاش مجدد برداشت', 'finance.deposit.forceCredit': 'واریز دستی',
  'trading.cancelOrder': 'لغو سفارش', 'trading.cancelAll': 'لغو همه سفارش‌ها',
  'trading.market': 'تنظیم بازار', 'kyc.approve': 'تأیید احراز هویت', 'kyc.reject': 'رد احراز هویت',
  'kyc.card.approve': 'تأیید کارت', 'kyc.card.reject': 'رد کارت',
  'chain.resync': 'بازخوانی بلاک‌ها', 'chain.sweep': 'جمع‌آوری موجودی',
  'settings.update': 'تغییر تنظیمات', 'announce.create': 'ایجاد اطلاعیه',
  'announce.broadcast': 'پیام همگانی', 'content.post.create': 'ایجاد مطلب',
  'content.post.delete': 'حذف مطلب', 'support.reply': 'پاسخ تیکت',
  'staking.plan.create': 'ایجاد طرح استیکینگ', 'staking.cancel': 'لغو استیکینگ',
}

export default function Logs() {
  const [rows, setRows] = useState<any[] | null>(null)
  const [meta, setMeta] = useState<any>(null)
  const [actions, setActions] = useState<string[]>([])
  const [f, setF] = useState({ q: '', action: '', page: 1 })
  const [loading, setLoading] = useState(false)
  const [view, setView] = useState<any>(null)

  const load = () => {
    setLoading(true)
    get(`/admin/system/logs${qs({ ...f, per_page: 40 })}`)
      .then((r: any) => { setRows(r.data); setMeta(r.meta); setActions(r.actions) })
      .catch(() => setRows([])).finally(() => setLoading(false))
  }
  useEffect(() => { const id = setTimeout(load, f.q ? 350 : 0); return () => clearTimeout(id) }, [f])

  return (
    <>
      <PageHead title="گزارش اقدامات مدیران" subtitle="ردیابی کامل هر تغییری که کارکنان روی سامانه انجام داده‌اند" />
      <Panel dense>
        <div className="p-4 pb-0">
          <Toolbar>
            <SearchBox value={f.q} onChange={(q) => setF({ ...f, q, page: 1 })} placeholder="ایمیل مدیر، شناسه هدف یا جزئیات…" />
            <Select value={f.action} onChange={(action) => setF({ ...f, action, page: 1 })}
              options={[['', 'همه اقدامات'], ...actions.map((a) => [a, LABELS[a] ?? a] as [string, string])]} />
            <RefreshButton onClick={load} loading={loading} />
          </Toolbar>
        </div>
        <DataTable rows={rows} loading={loading} minWidth={860} empty="گزارشی ثبت نشده است"
          onRowClick={(r: any) => setView(r)}
          columns={[
            { key: 'id', header: '#', cell: (r: any) => <span className="num text-slate-500">{fa(r.id)}</span> },
            { key: 'a', header: 'مدیر', cell: (r: any) => <span className="num text-slate-200" dir="ltr">{r.admin}</span> },
            { key: 'ac', header: 'اقدام', cell: (r: any) => <Tag kind="info">{LABELS[r.action] ?? r.action}</Tag> },
            {
              key: 't', header: 'هدف', cell: (r: any) => r.targetType === 'user'
                ? <Link to={`/admin/users/${r.targetId}`} className="num text-brand-300 hover:underline">کاربر #{fa(r.targetId)}</Link>
                : <span className="num text-slate-400">{r.targetType} {r.targetId}</span>,
            },
            { key: 'de', header: 'جزئیات', cell: (r: any) => <span className="block max-w-[22rem] truncate text-[11px] text-slate-500" dir="auto">{r.detail || '—'}</span> },
            { key: 'ip', header: 'IP', cell: (r: any) => <span className="num text-slate-500" dir="ltr">{r.ip || '—'}</span> },
            { key: 'd', header: 'زمان', cell: (r: any) => <span className="text-slate-500">{faDate(r.createdAt)}</span> },
          ]} />
        <Pager meta={meta} onPage={(page) => setF({ ...f, page })} />
      </Panel>

      <Drawer open={!!view} onClose={() => setView(null)} title="جزئیات اقدام" wide>
        {view && (
          <div className="space-y-3">
            {[['مدیر', view.admin], ['اقدام', LABELS[view.action] ?? view.action],
              ['نوع هدف', view.targetType || '—'], ['شناسه هدف', view.targetId || '—'],
              ['IP', view.ip || '—'], ['زمان', faDate(view.createdAt)]].map(([k, v]) => (
              <div key={k as string} className="rounded-xl border border-white/[.06] bg-white/[.02] p-3">
                <div className="mb-1 text-[10px] text-slate-500">{k}</div>
                <div className="num text-sm text-slate-200" dir="auto">{v}</div>
              </div>
            ))}
            <div className="rounded-xl border border-white/[.06] bg-ink-950/70 p-3">
              <div className="mb-1 text-[10px] text-slate-500">داده کامل</div>
              <pre className="num overflow-x-auto whitespace-pre-wrap break-all text-[11px] leading-6 text-slate-300" dir="ltr">
                {(() => { try { return JSON.stringify(JSON.parse(view.detail), null, 2) } catch { return view.detail || '—' } })()}
              </pre>
            </div>
          </div>
        )}
      </Drawer>
    </>
  )
}
